/**
 * GenetecPlayer.jsx
 * =================
 * Renders a Genetec Security Center camera stream using the Genetec Web Player (gwp.js).
 *
 * Props:
 *   cameraGuid        - Genetec camera GUID
 *   integrationId     - CCM integration ID (for token + gwp.js endpoints)
 *   mediaGatewayEndpoint - Full proxy URL to Media Gateway (via raw relay tunnel)
 *   gwpJsUrl          - URL to load gwp.js from (cached on CCM server)
 *   tokenEndpoint     - Backend token proxy endpoint
 *   autoPlay          - Start live on mount (default true)
 *   onPlayerReady     - Callback when player is started
 *   onError           - Callback on error
 *   style             - Container style overrides
 *   className         - Container class overrides
 */

import { useEffect, useRef, useState, useCallback } from 'react';

// Global gwp.js loading state (shared across all player instances)
let gwpLoadPromise = null;
let gwpLoadedUrl = null;

function loadGwpScript(url) {
  if (window.gwp && gwpLoadedUrl === url) {
    return Promise.resolve(window.gwp);
  }
  if (gwpLoadPromise && gwpLoadedUrl === url) {
    return gwpLoadPromise;
  }

  gwpLoadedUrl = url;
  gwpLoadPromise = (async () => {
    // Fetch with credentials so the authToken cookie is sent through the proxy
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch gwp.js: HTTP ' + res.status);
    const jsText = await res.text();

    // Create a blob URL and inject as script (gwp.js sets window.gwp)
    const blob = new Blob([jsText], { type: 'application/javascript' });
    const blobUrl = URL.createObjectURL(blob);

    await new Promise((resolve, reject) => {
      const existing = document.getElementById('gwp-script');
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.id = 'gwp-script';
      script.src = blobUrl;
      script.onload = () => {
        URL.revokeObjectURL(blobUrl);
        resolve();
      };
      script.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        reject(new Error('Failed to execute gwp.js'));
      };
      document.head.appendChild(script);
    });

    if (!window.gwp) throw new Error('gwp.js loaded but window.gwp not found');
    console.log('[GenetecPlayer] gwp.js loaded, version:', window.gwp.version || 'unknown');
    return window.gwp;
  })();

  return gwpLoadPromise;
}

export default function GenetecPlayer({
  cameraGuid,
  integrationId,
  mediaGatewayEndpoint,
  gwpJsUrl,
  tokenEndpoint,
  autoPlay = true,
  isLiveMode = true,
  playbackTime = null,
  muted = false,
  onPlayerReady,
  onError,
  style,
  className,
}) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [playerState, setPlayerState] = useState('loading'); // loading | connecting | live | playback | error | stopped
  const [streamStatus, setStreamStatus] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [frameTime, setFrameTime] = useState(null);

  // Build token retriever function
  // The GWP player calls this whenever it needs a fresh token for a camera
  const getTokenRetriever = useCallback(() => {
    const endpoint = tokenEndpoint || `/api/vms/genetec/${integrationId}/token/${cameraGuid}`;

    return async (cameraid) => {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error('Token request failed: HTTP ' + response.status);
        }
        const token = await response.text();
        return token;
      } catch (err) {
        console.error('[GenetecPlayer] Token retrieval failed:', err);
        throw err;
      }
    };
  }, [tokenEndpoint, integrationId, cameraGuid]);

  // Initialize and start player
  useEffect(() => {
    if (!containerRef.current || !cameraGuid || !mediaGatewayEndpoint) return;

    let disposed = false;
    console.log("[GenetecPlayer] MOUNTING for", cameraGuid);
    let player = null;

    async function init() {
      try {
        setPlayerState('loading');
        setErrorMsg('');

        // Load gwp.js
        const url = gwpJsUrl || `/api/vms/genetec/${integrationId}/gwp.js`;
        const gwp = await loadGwpScript(url);

        if (disposed) return;

        // Build player
        player = gwp.buildPlayer(containerRef.current);
        playerRef.current = player;

        // Register events
        player.onPlayerStateChanged.register((state) => {
          if (disposed) return;
          const val = state.value || state.playerState || '';
          console.log('[GenetecPlayer] State:', val);
          if (String(val).toLowerCase().includes('error')) {
            setPlayerState('error');
            setErrorMsg(String(val));
          }
        });

        player.onPlayerModeChanged.register((mode) => {
          if (disposed) return;
          const m = mode.playerMode || '';
          console.log('[GenetecPlayer] Mode:', m);
          if (m === 'live') setPlayerState('live');
          else if (m === 'playback') setPlayerState('playback');
        });

        player.onStreamStatusChanged.register((status) => {
          if (disposed) return;
          const val = status.value || '';
          console.log('[GenetecPlayer] Stream status:', val);
          setStreamStatus(String(val));
        });

        player.onFrameRendered.register((date) => {
          if (disposed) return;
          setFrameTime(date);
        });

        player.onErrorStateRaised.register((error) => {
          if (disposed) return;
          const msg = 'Error ' + (error.errorCode || '') + ': ' + (error.value || '');
          console.error('[GenetecPlayer] Error:', msg);
          setPlayerState('error');
          setErrorMsg(msg);
          if (onError) onError(msg);
        });

        // Start the player
        setPlayerState('connecting');
        await player.start(cameraGuid, mediaGatewayEndpoint, getTokenRetriever());

        if (disposed) {
          player.dispose();
          return;
        }

        console.log('[GenetecPlayer] Connected to Media Gateway');
        if (onPlayerReady) onPlayerReady(player);

        // Auto-play live
        if (autoPlay) {
          player.playLive();
          setPlayerState('live');
        }
      } catch (err) {
        console.error('[GenetecPlayer] Init failed:', err);
        if (!disposed) {
          setPlayerState('error');
          setErrorMsg(err.message);
          if (onError) onError(err.message);
        }
      }
    }

    init();

    return () => {
      disposed = true;
      console.log("[GenetecPlayer] UNMOUNTING for", cameraGuid);
      if (player) {
        try {
          if (player.isStarted) player.stop();
          player.dispose();
        } catch (e) {
          console.warn('[GenetecPlayer] Cleanup error:', e);
        }
      }
      playerRef.current = null;
    };
  }, [cameraGuid, mediaGatewayEndpoint, integrationId, gwpJsUrl, autoPlay]);


  // Handle live/playback mode switches and seeks without remounting
  // Track last seek time to avoid spamming seek() on every 1-second playback tick
  const lastSeekTimeRef = useRef(null);
  useEffect(() => {
    if (!playerRef.current || !playerRef.current.isStarted) return;
    if (isLiveMode) {
      console.log('[GenetecPlayer] Switching to live');
      lastSeekTimeRef.current = null;
      playerRef.current.playLive();
    } else if (playbackTime) {
      const seekMs = playbackTime instanceof Date ? playbackTime.getTime() : new Date(playbackTime).getTime();
      const lastMs = lastSeekTimeRef.current;
      // Only seek if this is the first seek or the jump is more than 3 seconds
      // (1-second ticks from the parent timer are ignored -- GWP handles continuous playback)
      if (lastMs === null || Math.abs(seekMs - lastMs) > 3000) {
        console.log('[GenetecPlayer] Seeking to', new Date(seekMs));
        playerRef.current.seek(new Date(seekMs));
        lastSeekTimeRef.current = seekMs;
      } else {
        // Update tracking without seeking -- GWP is already playing continuously
        lastSeekTimeRef.current = seekMs;
      }
    }
  }, [isLiveMode, playbackTime]);

  // Public control methods (exposed via ref or callbacks)
  const playLive = useCallback(() => {
    if (playerRef.current && playerRef.current.isStarted) {
      playerRef.current.playLive();
    }
  }, []);

  const seek = useCallback((date) => {
    if (playerRef.current && playerRef.current.isStarted) {
      playerRef.current.seek(date);
    }
  }, []);

  const pause = useCallback(() => {
    if (playerRef.current && playerRef.current.isStarted) {
      playerRef.current.pause();
    }
  }, []);

  const resume = useCallback(() => {
    if (playerRef.current && playerRef.current.isStarted) {
      playerRef.current.resume();
    }
  }, []);

  // Status overlay text
  const getStatusText = () => {
    switch (playerState) {
      case 'loading': return 'Loading player...';
      case 'connecting': return 'Connecting to Media Gateway...';
      case 'error': return errorMsg || 'Connection error';
      case 'stopped': return 'Stopped';
      default: return null;
    }
  };

  const statusText = getStatusText();

  return (
    <div
      className={`genetec-player-container ${className || ''}`}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: '#000',
        borderRadius: '6px',
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* gwp.js injects its canvas/video element into this div */}
      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%' }}
      />

      {/* Status overlay (shown when not actively streaming) */}
      {statusText && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.7)',
            color: playerState === 'error' ? '#ef4444' : '#e2e8f0',
            fontSize: '13px',
            textAlign: 'center',
            padding: '16px',
            pointerEvents: playerState === 'error' ? 'auto' : 'none',
          }}
        >
          <div>
            {playerState === 'loading' || playerState === 'connecting' ? (
              <div className="loading-spinner" style={{ marginBottom: '8px' }} />
            ) : null}
            <p style={{ margin: 0 }}>{statusText}</p>
            {playerState === 'error' && (
              <button
                onClick={() => {
                  // Force re-mount by toggling state
                  setPlayerState('loading');
                  setErrorMsg('');
                  // The useEffect will re-run on next render if deps change
                  // For now, reload the page section
                  window.location.reload();
                }}
                style={{
                  marginTop: '8px',
                  padding: '6px 16px',
                  background: '#1d4ed8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Named export for control methods pattern
export { GenetecPlayer };
